// Catalogue technique et réglages de la Proposition TRAME : types, libellés et petits outils partagés par les trois onglets.
// Aucune valeur de fabricant n'est proposée ici : une case vide reste « à relever sur la notice ».
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { api } from '../../../lib/api.ts';
import { Bouton, Champ, ChampChoix } from '../../../components/ui.tsx';
import { DIAMETRES_FRIGORIFIQUES, type DiametreFrigorifique } from '@shared/frigorifique.ts';
import type { ModeleProposition, TypeModele, TypeUi } from '@shared/proposition/types.ts';

export type Source = { document: string; page: number };
export type ModeleVue = ModeleProposition & {
  prixId: string | null; prixArticle: { reference: string | null; libelle: string | null } | null; raccordRefoulement: DiametreFrigorifique | null;
  actif: boolean; sources: Record<string, Source>; validation: { le: string } | null; majLe: string;
};
export interface ArticlePrix { id: string; reference: string | null; libelle: string | null }

export const LIBELLES_TYPE: Record<TypeModele, string> = {
  cassette_4v: 'Cassette 4 voies', cassette_1v: 'Cassette 1 voie', cassette_2v: 'Cassette 2 voies', gainable_bp: 'Gainable basse pression',
  gainable_mp: 'Gainable moyenne pression', gainable_hp: 'Gainable haute pression', murale: 'Murale', console: 'Console', plafonnier: 'Plafonnier',
  console_plafonnier: 'Console-plafonnier', ue_drv_2t: 'Unité extérieure DRV 2 tubes', ue_drv_3t: 'Unité extérieure DRV 3 tubes',
  ue_multisplit: 'Unité extérieure multisplit', ue_split: 'Unité extérieure split', boitier: 'Boîtier de sélection',
};
export const LIBELLES_ROLE: Record<string, string> = { ui: 'Unité intérieure', ue: 'Unité extérieure', module_ue: 'Module d’unité extérieure', boitier: 'Boîtier' };
export const libelleTypeUi = (t: TypeUi) => LIBELLES_TYPE[t];

/** Nombre saisi (virgule acceptée) ; vide = null ; non numérique = NaN (refusé avant l'envoi). */
export const nombre = (v: string): number | null => { const t = v.trim().replace(/\s/g, '').replace(',', '.'); return t === '' ? null : Number(t); };
export const texteVide = (v: string) => (v.trim() === '' ? null : v.trim());
export const enTexte = (v: number | null | undefined) => (v == null ? '' : String(v).replace('.', ','));
export const fmt = (v: number | null | undefined, unite = '') => (v == null ? null : `${v.toLocaleString('fr-FR')}${unite ? ` ${unite}` : ''}`);

/** Diamètre frigorifique choisi par sa désignation en pouces ; un tube métrique déjà saisi reste proposé tel quel. */
export function ChoixDiametre({ libelle, valeur, changer, disabled }: { libelle: string; valeur: DiametreFrigorifique | null; changer: (d: DiametreFrigorifique | null) => void; disabled?: boolean }) {
  const metrique = valeur && valeur.norme === 'metrique' ? valeur : null;
  const options = [{ valeur: '', libelle: 'À relever sur la notice' }, ...DIAMETRES_FRIGORIFIQUES.map(([p, mm]) => ({ valeur: p, libelle: `${p}″ (${mm.replace('.', ',')} mm)` })),
    ...(metrique ? [{ valeur: `mm:${metrique.mm}`, libelle: `Ø ${metrique.mm} mm (métrique)` }] : [])];
  const v = metrique ? `mm:${metrique.mm}` : valeur?.pouces ?? '';
  return <ChampChoix libelle={libelle} value={v} disabled={disabled} options={options} onChange={(e) => {
    const x = e.target.value;
    if (!x) return changer(null);
    if (x.startsWith('mm:')) return changer(metrique);
    const d = DIAMETRES_FRIGORIFIQUES.find(([p]) => p === x)!;
    changer({ pouces: d[0], mm: d[1] });
  }} />;
}
export const libelleDiametre = (d: DiametreFrigorifique | null | undefined) => (!d ? null : d.norme === 'metrique' || !d.pouces ? `Ø ${d.mm} mm` : `${d.pouces}″`);

/** Oui / non / à relever (booléen d'une notice ou d'une fiche : null = donnée absente, jamais « non »). */
export function ChoixOuiNon({ libelle, valeur, changer, disabled, aide }: { libelle: string; valeur: boolean | null; changer: (v: boolean | null) => void; disabled?: boolean; aide?: string }) {
  return <ChampChoix libelle={libelle} aide={aide} disabled={disabled} value={valeur == null ? '' : valeur ? 'oui' : 'non'}
    options={[{ valeur: '', libelle: 'À relever sur la notice' }, { valeur: 'oui', libelle: 'Oui' }, { valeur: 'non', libelle: 'Non' }]}
    onChange={(e) => changer(e.target.value === '' ? null : e.target.value === 'oui')} />;
}

/**
 * Article du catalogue de prix relié (référence et désignation ; le prix n'est jamais affiché ici).
 * Recherche dans le catalogue de prix de l'entreprise ; aucun article n'est créé depuis cet écran.
 */
export function ChoixArticlePrix({ libelle, valeur, changer, disabled }: { libelle: string; valeur: ArticlePrix | null; changer: (a: ArticlePrix | null) => void; disabled?: boolean }) {
  const [q, setQ] = useState('');
  const [attendu, setAttendu] = useState('');
  useEffect(() => { const t = setTimeout(() => setAttendu(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const r = useQuery({ queryKey: ['prix-recherche', attendu], enabled: attendu.length >= 2,
    queryFn: () => api.get<{ elements: { id: string; reference: string; label: string; supplier_name: string | null }[] }>(`/api/prix?q=${encodeURIComponent(attendu)}&limite=8`) });
  return <div className="pile ctech-article">
    <span className="champ-libelle">{libelle}</span>
    {valeur ? <div className="rangee ctech-article-choisi">
      <span className="ctech-article-texte"><strong className="mono">{valeur.reference ?? 'Référence inconnue'}</strong> {valeur.libelle ? `— ${valeur.libelle}` : ''}</span>
      {!disabled && <Bouton compact icone={<X aria-hidden />} onClick={() => changer(null)}>Retirer</Bouton>}
    </div> : <span className="petit discret">Aucun article relié : prix « à renseigner » dans la proposition.</span>}
    {!disabled && <>
      <Champ libelle="Chercher dans le catalogue de prix" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Référence ou désignation" />
      {attendu.length >= 2 && r.data && <div className="pile ctech-resultats" role="list">
        {!r.data.elements.length && <p className="petit discret">Aucun article trouvé.</p>}
        {r.data.elements.map((a) => <button key={a.id} type="button" role="listitem" className="btn btn-fantome ctech-resultat"
          onClick={() => { changer({ id: a.id, reference: a.reference, libelle: a.label }); setQ(''); }}>
          <Search aria-hidden /><span><strong className="mono">{a.reference}</strong> — {a.label}{a.supplier_name ? ` · ${a.supplier_name}` : ''}</span></button>)}
      </div>}
    </>}
  </div>;
}
